import { memo, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { message, Modal } from 'antd';
import { UserInput } from '@/base-components';
import { sendMessage, initializeSession, createNewSession } from '@@store/slices/chatSlice';
import routePath from '@/routes/route-path';
import {
    getHomeQuestions,
    getUserAgreement,
    getStoredSerialNumber,
    getStoredDataParam,
    getUrlParamsAsObject,
    reportUserAgreementConsent,
    setStoredCountry
} from '@@service/api';
import type { AppDispatch, RootState } from '@@store/index';
import type { UserAgreementResponse } from '@/types/session';
import { useDevice } from '@/hooks';
import CountrySelector from '@/components/CountrySelector';
import type { CountryOption } from '@/components/CountrySelector';
import './index.less';

const AGREEMENT_ACCEPT_KEY = 'user_agreement_accepted_v1';
const AGREEMENT_EXPIRE_DAYS = 30;

// 检查协议是否在有效期内（30天）
const isAgreementValid = (): boolean => {
    const storedData = localStorage.getItem(AGREEMENT_ACCEPT_KEY);
    if (!storedData) return false;
    try {
        const { timestamp } = JSON.parse(storedData);
        const now = Date.now();
        const expireMs = AGREEMENT_EXPIRE_DAYS * 24 * 60 * 60 * 1000;
        return (now - timestamp) < expireMs;
    } catch {
        return false;
    }
};

const Home = memo(() => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    const [ inputStr, setInputStr ] = useState('');
    const [ greetingText, setGreetingText ] = useState('');
    const [ questions, setQuestions ] = useState<string[]>([]);
    const [ agreementOpen, setAgreementOpen ] = useState(false);
    const [ agreementData, setAgreementData ] = useState<UserAgreementResponse | null>(null);
    const [ countryOpen, setCountryOpen ] = useState(false);
    const [ selectedCountry, setSelectedCountry ] = useState<CountryOption | null>(null);

    // 记录本次进入是否来自“新建会话”。true 时强制 createNewSession 而不是 restore
    const forceNewRef = useRef<boolean>(false);

    const layoutMode = useDevice();

    const {
        isLoading,
        currentSessionId,
        status
    } = useSelector((state: RootState) => state.chat);
    const curTheme = useSelector((state: RootState) => state.theme);

    // 并行调用 session/create + get-recommend-questions
    const startSessionAndGetQuestions = async (countryCode: string) => {
        const isForceNew = forceNewRef.current;
        const sessionThunk = isForceNew
            ? createNewSession({ country: countryCode })
            : initializeSession({ country: countryCode });
        const [ sessionResult, questionsData ] = await Promise.all([
            dispatch(sessionThunk as any).unwrap(),
            getHomeQuestions()
        ]);
        // 用完即清，下次默认走 initializeSession
        forceNewRef.current = false;
        // 恢复会话且有历史消息 → 直接跳转 Chat 页；强制新建分支不会进入该判断
        if (!isForceNew && (sessionResult as any)?.isRestored && (sessionResult as any)?.messages?.length > 0) {
            navigate(routePath.chat);
            return;
        }
        setGreetingText(questionsData?.greetingText || '');
        setQuestions(questionsData?.greetingQuestion || []);
    };

    const handleAcceptAgreement = async () => {
        // 点击确认：记录时间戳（30天有效期）
        const acceptData = JSON.stringify({ accepted: true, timestamp: Date.now() });
        localStorage.setItem(AGREEMENT_ACCEPT_KEY, acceptData);
        try {
            await reportUserAgreementConsent({
                accepted: true,
                serial_number_base64: getStoredDataParam()
            });
        } catch (error) {
            console.error('上报用户协议接受状态失败', error);
        }
        setAgreementOpen(false);

        // 同意协议后，用已选中的国家创建会话并获取推荐问题
        if (selectedCountry) {
            startSessionAndGetQuestions(selectedCountry.code);
        }
    };

    const handleCountryConfirm = (country: CountryOption) => {
        setCountryOpen(false);
        setSelectedCountry(country);
        setStoredCountry(country.code);

        // 地区选择完成后，检查协议状态
        const accepted = isAgreementValid();
        if (!accepted) {
            // 未同意协议 → 弹出协议弹窗
            setAgreementOpen(true);
            getUserAgreement().then(data => {
                setAgreementData(data);
            }).catch(() => {
                setAgreementData(null);
            });
        } else {
            // 已同意协议 → 直接并行创建会话+获取推荐问题
            startSessionAndGetQuestions(country.code);
        }
    };

    const handleRejectAgreement = async () => {
        try {
            await reportUserAgreementConsent({
                accepted: false,
                serial_number_base64: getStoredDataParam()
            });
        } catch (error) {
            console.error('上报用户协议拒绝状态失败', error);
        }
        const targetUrl = agreementData?.refuse_button_url;
        if (!targetUrl) return;
        const serialNumber = getStoredSerialNumber();
        const finalUrl = serialNumber
            ? `${targetUrl}?SerialNumber=${encodeURIComponent(serialNumber)}`
            : targetUrl;
        window.location.href = finalUrl;
    };

    const handleSend = async (value: string, attachments?: string[]) => {
        if (!value.trim() && (!attachments || attachments.length === 0)) return;

        if (!currentSessionId) {
            message.error('没有活跃的会话，请先创建新对话');
            return;
        }

        try {
            // 先跳转到chat页
            navigate(routePath.chat);

            // 然后发送消息
            await dispatch(sendMessage({
                content: value,
                attachments
            })).unwrap();

        } catch (error) {
            message.error('发送消息失败，请重试');
        }
    };

    // 首次挂载：始终先弹出地区选择；若来自”新建会话”，跳过国家弹窗
    useEffect(() => {
        const navState: any = (location && (location as any).state) || {};
        if (navState.fromNewChat) {
            // session 已在 Sidebar 创建，仅加载推荐问题
            getHomeQuestions().then(data => {
                setGreetingText(data?.greetingText || '');
                setQuestions(data?.greetingQuestion || []);
            });
        } else {
            setCountryOpen(true);
        }
    }, []);

    // 清理路由 state
    useEffect(() => {
        const navState: any = (location && (location as any).state) || {};
        const hasNavFlag = !!navState
            && (navState.showAgreement || navState.suppressAgreement || navState.fromNewChat);
        if (hasNavFlag) {
            navigate(routePath.home, { replace: true });
        }
    }, [ location.key ]);

    return (
        <div
            className={`home-page ${curTheme}-home-page custom-scrollbar`}
            style={{
                padding: layoutMode.isMobile ? '12px' : '16px 80px'
            }}
        >
            <h1 className="visually-hidden">Agentic Intelligent Resolver Home</h1>
            <div
                className="home-page-content"
                style={{
                    width: layoutMode.isMobile ? '100%' : '60%',
                    height: layoutMode.isMobile ? '100%' : undefined,
                    alignItems: layoutMode.isMobile ? 'center' : 'start',
                    justifyContent: 'center'
                }}
            >
                <div
                    className="content-header"
                    style={{
                        width: layoutMode.isMobile ? '80%' : '100%',
                        alignItems: layoutMode.isMobile ? 'center' : 'start',
                        justifyContent: 'center'
                    }}
                >
                    <div className="img-box">
                        <img
                            src={require(`@/assets/bot-home-${curTheme}.png`)}
                            width={113}
                            height={113}
                            alt="robot"
                        />
                    </div>
                    {greetingText && (
                        <div
                            className={layoutMode.isMobile ? 'font-s-14' : 'font-s-20'}
                            style={{ textAlign: layoutMode.isMobile ? 'center' : undefined }}
                            dangerouslySetInnerHTML={{ __html: greetingText }}
                        />
                    )}
                </div>
                {

                    !layoutMode.isMobile && questions.length > 0 && (
                        <div className="content-list">
                            {
                                questions.map((q, index) => (
                                    <div
                                        key={index}
                                        className="content-list-card"
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Select recommended question: ${q}`}
                                        onClick={() => setInputStr(q)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setInputStr(q);
                                            }
                                        }}
                                    >
                                        {q}
                                    </div>
                                ))
                            }
                        </div>
                    )
                }
            </div>
            <UserInput
                style={{
                    width: layoutMode.isMobile ? '100%' : '60%'
                }}
                inputValue={inputStr}
                onChange={setInputStr}
                onSubmit={handleSend}
                loading={isLoading || status === 'streaming' || status === 'submitted'}
                disabled={!currentSessionId || status === 'streaming' || status === 'submitted'}
                maxLength={400}
                maxFileCount={1}
            />
            <Modal
                open={agreementOpen}
                onOk={handleAcceptAgreement}
                onCancel={handleRejectAgreement}
                okText={agreementData?.button_content || 'Accept'}
                cancelText={agreementData?.refuse_button_content || 'Decline'}
                wrapClassName={`user-agreement-modal ${curTheme}-user-agreement-modal ${layoutMode.isMobile ? 'mobile-agreement-modal' : 'pc-agreement-modal'}`}
                cancelButtonProps={{
                    'aria-label': agreementData?.refuse_button_content || 'Decline user agreement'
                } as any}
                maskClosable={false}
                keyboard={false}
                closable={false}
                centered={true}
                title={agreementData?.introduction || 'User Agreement'}
                okButtonProps={{
                    'aria-label': agreementData?.button_content || 'Accept user agreement'
                } as any}
            >
                <div
                    className="agreement-body-text"
                    dangerouslySetInnerHTML={{
                        __html: agreementData?.confirm_select_content ?? ''
                    }}
                />
            </Modal>
            <CountrySelector
                open={countryOpen}
                onConfirm={handleCountryConfirm}
                urlCountryCode={(() => { const c = getUrlParamsAsObject()?.countryCode; if (!c) return c; const upper = c.toUpperCase(); return upper === 'UK' ? 'GB' : upper; })()}
            />
        </div>
    );
});

export default Home;
